import {
    useState,
    useCallback,
} from 'react';

import useDidUpdate from './useDidUpdate';

/**
 *
 * @description Hook that returns a paginated list of items and methods
 *
 * @param {Object}      options             Pagination options
 * @param {array}       options.list        Paginated List
 * @param {number}      options.limit       Number of items per page
 * @param {boolean}     options.hasMore     API service has more data
 * @param {function}    options.fetchMore   Callback to fetch more data from the service
 * @param {boolean}     options.isLoading   Data is loading
 *
 * @returns {{paginatedList: Array, hasMoreItems: boolean, getMoreItems: function, resetPagination: function}}  A paginatedList and a hasMoreItems boolean indicating if there is more items to paginate.
 *
 */
const usePagination = (options) => {

    const {
        list,
        limit,
        hasMore,
        fetchMore,
        isLoading,
    } = options;

    const [
        currentPage,
        setCurrentPage,
    ] = useState(1);

    const [
        paginatedList,
        setPaginatedList,
    ] = useState(list.slice(0, currentPage * limit));

    useDidUpdate(() => {

        if(isLoading) {

            return;

        }

        const availableItems = list.slice((currentPage - 1) * limit);

        if (list.length > 0 && availableItems.length < limit && hasMore) {

            return fetchMore();

        }

        setPaginatedList(list.slice(0, currentPage * limit));

    }, [
        list,
        currentPage,
        limit,
    ]);

    const hasMoreItems = hasMore || paginatedList.length !== list.length;

    const getMoreItems = useCallback(() => {

        setCurrentPage((currentPage) => currentPage + 1);

    }, [setCurrentPage]);

    const resetPagination = useCallback(() => {

        setCurrentPage(1);

    }, [setCurrentPage]);

    return {
        paginatedList,
        hasMoreItems,
        getMoreItems,
        resetPagination,
    };

};

export default usePagination;
